// purpose: Register all SendMoney routes.
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { View, TouchableOpacity, Text, StyleSheet, Platform, Modal } from "react-native";
import BackIcon from "../../../../../../../assets/images/BackIcon.svg";
import HelpIcon from "../../../../../../../assets/images/HelpIcon.svg";
import CloseIcon from "../../../../../../../assets/images/CloseIcon.svg";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";
import NeedHelpComponent from "../../common/NeedHelpComponent";
import { PhoneScreen } from "../pages/PhoneScreen";
import { CountryScreen } from "../pages/CountryScreen";
import { StateScreen } from "../pages/StateScreen";
import { HowTheyReceiveScreen } from "../pages/HowTheyReceiveScreen";
import { ReviewAndEditScreen } from "../pages/ReviewAndEditScreen";
import { SenderInfoScreen } from "../pages/SenderInfoScreen";
import { SenderAddressScreen } from "../pages/SenderAddressScreen";
import { SenderDobScreen } from "../pages/SenderDobScreen";
import { SenderID1Screen } from "../pages/SenderID1Screen";
import { SenderID2Screen } from "../pages/SenderID2Screen";
import { ReceiverInfoScreen } from "../pages/ReceiverInfoScreen";
import { AdditionalInfoScreen } from "../pages/AdditionalInfoScreen";
import { AmountEntryScreen } from "../pages/AmountEntryScreen";
import { SEND_MONEY_ROUTES } from "./sendMoneyRoutes";
import { useWorkflowInitialization } from "../hooks/useWorkflowInitialization";
import { useJourneyStore } from "../store/sendmoneyJourneyStore";
import { useWorkflowContextStore } from "../store/sendmoneyWorkflowContextStore";
import { useWorkflowStore } from "../store/sendmoneyWorkflowStore";
import { FraudScreen } from "../pages/FraudScreen";
import { FinalReviewScreen } from "../pages/FinalReviewScreen";
import { useWorkflowNavigation } from "../hooks/useWorkflowNavigation";
import { useStepActionStore } from "../store/sendmoneyStepActionStore";
import { HeadToCashierScreen } from "../pages/HeadToCashierScreen";
//import { HeadToCashierScreen } from "../pages/HeadToCashierScreen";

function PlaceholderScreen() {
  return null;
}
///Added a SendMoneyLayout wrapper On each route change it logs all three stores to the console:
//It runs whenever location.pathname changes, which happens when the user navigates to a new route.
// [SendMoney] stores {
//   journeyStore: { journeyData, updateJourneyData, resetJourneyData },
//   workflowContextStore: { workflowContext, updateWorkflowContext },
//   workflowStore: { activeWorkflow, currentStepKey, isEditMode, ... }
// }

function SendMoneyLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { goNext, goBack } = useWorkflowNavigation();
  const activeWorkflow = useWorkflowStore((s) => s.activeWorkflow);
  const currentStepKey = useWorkflowStore((s) => s.currentStepKey);
  const currentStep = activeWorkflow.findIndex((s) => s.key === currentStepKey);
  const resetJourneyData = useJourneyStore((s) => s.resetJourneyData);
  const [needHelp, setNeedHelp] = useState(false);

  const closeNeedHelp = () => {
    setNeedHelp(false);
  };

  const handleClose = () => {
    resetJourneyData();
    navigate("/");
  };

  useEffect(() => {
    console.log("[SendMoney] stores", {
      journeyStore: useJourneyStore.getState(),
      workflowContextStore: useWorkflowContextStore.getState(),
      workflowStore: useWorkflowStore.getState(),
    });
  }, [location.pathname]);

  // Master Next button: invoke the active screen's handler, or just advance.
  const handleNext = () => {
    const handler = useStepActionStore.getState().handler;
    if (handler) {
      handler();
    } else {
      goNext();
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.topBar}>
          {currentStep > 0 ? (
            <TouchableOpacity
              style={styles.topBarButton}
              onPress={goBack}
              testID="sendMoneyBackButton"
            >
              <BackIcon width={24} height={24} />
            </TouchableOpacity>
          ) : (
            <View style={styles.topBarButton} />
          )}
          <View style={styles.topBarActions}>
            <TouchableOpacity
              onPress={() => setNeedHelp(true)}
              style={styles.topBarButton}
              testID="sendMoneyHelpButton"
            >
              <HelpIcon width={24} height={24} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.topBarButton}
              onPress={handleClose}
              testID="sendMoneyCloseButton"
            >
              <CloseIcon width={24} height={24} />
            </TouchableOpacity>
          </View>
        </View>
        <View style={styles.progressRow}>
          {activeWorkflow.map((_, i) => (
            <View
              key={i}
              style={[
                styles.progressSegment,
                {
                  borderTopLeftRadius: i === 0 ? 9 : 0,
                  borderBottomLeftRadius: i === 0 ? 9 : 0,
                },
                {
                  borderTopRightRadius: i === activeWorkflow.length - 1 ? 9 : 0,
                  borderBottomRightRadius:
                    i === activeWorkflow.length - 1 ? 9 : 0,
                },
                {
                  backgroundColor:
                    i <= currentStep
                      ? Colors.titleTextColor
                      : Colors.searchBackground,
                },
              ]}
            />
          ))}
        </View>
      </View>
      <Outlet />
      <View style={styles.footerContainer}>
        <TouchableOpacity
          testID="sendMoneyNextButton"
          style={styles.button}
          onPress={handleNext}
        >
          <Text style={styles.buttonText}>Next</Text>
        </TouchableOpacity>
      </View>
      <Modal
        visible={needHelp}
        transparent
        animationType="none"
        onRequestClose={() => closeNeedHelp?.()}
      >
        <NeedHelpComponent closeNeedHelp={closeNeedHelp} />
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, flexDirection: "column" },
  header: { paddingHorizontal: 24 },
  topBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
  },
  topBarButton: { padding: 1 },
  topBarActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  progressRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    height: 4,
  },
  progressSegment: { flex: 1, height: 4 },
  footerContainer: {
    backgroundColor: Colors.white,
    borderTopWidth: 1,
    borderColor: "#F2F2F2",
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 14,
  },
  button: {
    backgroundColor: Colors.black,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 14,
    marginBottom: Platform.OS === "ios" ? 24 : 16,
  },
  buttonText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "600",
    color: Colors.white,
    lineHeight: 20,
  },
});

/** Paths are relative to the parent `/sendMoney/*` route in App.tsx. */
const SEND_MONEY_ROUTE_SEGMENTS = {
  PHONE: "phone",
  PIN: "pin",
  COUNTRY: "country",
  STATE: "state",
  AMOUNT: "amount",
  HOW_THEY_RECEIVE: "how-they-receive",
  REVIEW_AND_EDIT: "review-and-edit",
  SENDER_INFO: "sender-info",
  SENDER_ADDRESS: "sender-address",
  SENDER_DOB: "sender-dob",
  SENDER_ID1: "sender-id-1",
  SENDER_ID2: "sender-id-2",
  RECEIVER_INFO: "receiver-info",
  FRAUD_PREVENTION: "fraud-prevention",
  ADDITIONAL_INFO: "additional-info",
  FINAL_REVIEW: "final-review",
  HEAD_TO_CASHIER: "head-to-cashier",
} as const;

export function SendMoneyRouter() {
  useWorkflowInitialization();

  return (
    <Routes>
      <Route element={<SendMoneyLayout />}>
        <Route index element={<Navigate to={SEND_MONEY_ROUTE_SEGMENTS.PHONE} replace />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.PHONE} element={<PhoneScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.PIN} element={<PlaceholderScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.COUNTRY} element={<CountryScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.STATE} element={<StateScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.AMOUNT} element={<AmountEntryScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.HOW_THEY_RECEIVE} element={<HowTheyReceiveScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.REVIEW_AND_EDIT} element={<ReviewAndEditScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.SENDER_INFO} element={<SenderInfoScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.SENDER_ADDRESS} element={<SenderAddressScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.SENDER_DOB} element={<SenderDobScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.SENDER_ID1} element={<SenderID1Screen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.SENDER_ID2} element={<SenderID2Screen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.RECEIVER_INFO} element={<ReceiverInfoScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.FRAUD_PREVENTION} element={<FraudScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.ADDITIONAL_INFO} element={<AdditionalInfoScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.FINAL_REVIEW} element={<FinalReviewScreen />} />
        <Route path={SEND_MONEY_ROUTE_SEGMENTS.HEAD_TO_CASHIER} element={<HeadToCashierScreen />} />
        <Route path="*" element={<Navigate to={SEND_MONEY_ROUTES.PHONE} replace />} />
      </Route>
    </Routes>
  );
}


